import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";
import { InlineEligibilityForm } from "@/components/InlineEligibilityForm";

const faqs: FAQItem[] = [
  {
    question: "What is the PHQ-9?",
    answer:
      "The PHQ-9 is a nine-item questionnaire that measures the severity of depression symptoms over the past two weeks. Each item is scored 0 (not at all) to 3 (nearly every day), for a total range of 0 to 27. Scores are grouped into bands: minimal (0–4), mild (5–9), moderate (10–14), moderately severe (15–19), and severe (20–27). It is one of the most widely used screening and tracking tools in mental health care.",
  },
  {
    question: "What is the GAD-7?",
    answer:
      "The GAD-7 is a seven-item questionnaire that measures anxiety symptoms over the past two weeks, using the same 0-to-3 scale as the PHQ-9. The total ranges from 0 to 21: minimal (0–4), mild (5–9), moderate (10–14), and severe (15–21). It is commonly given alongside the PHQ-9 because depression and anxiety frequently overlap.",
  },
  {
    question:
      "Can I have a 'moderate' score and still be diagnosed with severe depression?",
    answer:
      "Yes. The PHQ-9 is a two-week snapshot of symptom frequency. A clinical diagnosis takes in the full picture: how long the depression has lasted, how many episodes you've had, how much it has affected your life, and how you've responded to treatment. Someone whose treatment is partly working may score in the moderate range while still meeting criteria for severe, recurrent, treatment-resistant depression.",
  },
  {
    question: "What PHQ-9 score means remission?",
    answer:
      "In depression research and in TMS clinics, remission is typically defined as a PHQ-9 score below 5. That is the target — not just feeling a little better, but dropping below the threshold where you no longer meet criteria for depression.",
  },
  {
    question: "Why has my score been stuck for months?",
    answer:
      "If your PHQ-9 has stayed in the same range across two or more medication trials, that is data telling you the treatments tried so far are not reaching the problem. Antidepressants work on brain chemistry, but depression is also a circuitry problem. When the mood-regulating circuit stays underactive, adjusting the chemical environment around it may not move the score. That is often when a circuit-level treatment like TMS or SAINT is considered.",
  },
  {
    question: "Do the PHQ-9 and GAD-7 diagnose me?",
    answer:
      "No. These questionnaires are screening and tracking tools, not diagnostic tests. They flag when someone should be evaluated further, establish a baseline so progress can be measured, and track whether treatment is working over time. Your diagnosis is a clinical judgment made by a qualified provider.",
  },
];

const related: RelatedLink[] = [
  {
    title: "You've Tried Everything. Why Is the Depression Still Here?",
    href: "/youve-tried-everything-why-is-the-depression-still-here",
    description:
      "What treatment-resistant depression means and why a circuit-level approach may help.",
  },
  {
    title: "SAINT TMS, Plainly: What It Is, Who It's For, and What Happens",
    href: "/saint-tms-plainly-what-it-is-who-its-for-and-what-happens",
    description:
      "A plain-language guide to SAINT TMS, eligibility, and the treatment process.",
  },
  {
    title: "Am I a Candidate for TMS?",
    href: "/am-i-a-candidate",
    description: "What clinicians consider when evaluating TMS eligibility.",
  },
  {
    title: "SAINT® TMS in Coral Springs, FL",
    href: "/saint",
    description: "The full SAINT treatment page with outcomes, cost, and FAQs.",
  },
];

export default function UnderstandingPhq9Gad7() {
  return (
    <DepressionContentPage
      seo={{
        title: "Understanding Your PHQ-9 & GAD-7 Scores | Neuro Wellness",
        description:
          "What PHQ-9 and GAD-7 scores mean, why 'moderate' can still mean severe depression, and when a stuck score signals it's time to look beyond medication.",
        canonical: "/understanding-your-phq-9-and-gad-7-scores",
        keywords:
          "PHQ-9 score, GAD-7 score, depression screening, anxiety screening, moderate depression, severe depression, remission PHQ-9, treatment-resistant depression, depression questionnaire, TMS tracking",
      }}
      badge="Clinical Insights"
      heroTitle="Understanding Your PHQ-9 and GAD-7 Scores (And Why 'Moderate' Can Still Mean Severe)"
      heroSubtitle="At some point in your treatment, someone handed you a clipboard with two short questionnaires. You circled numbers, handed it back, and maybe glanced at a total. Understanding what those numbers mean — and what they don't — helps you have better conversations with your treatment team."
      lastUpdated="2026-11-03"
      sections={[
        {
          heading: "The PHQ-9: measuring depression",
          body: (
            <div className="space-y-4">
              <p>
                The PHQ-9 asks how often, over the last two weeks, you've been
                bothered by nine things: low interest or pleasure, feeling down,
                sleep problems, low energy, appetite changes, feeling bad about
                yourself, trouble concentrating, being slowed down or restless,
                and thoughts of being better off dead or hurting yourself.
              </p>
              <p>
                Each item is scored 0 (not at all) to 3 (nearly every day). The
                total ranges from 0 to 27.
              </p>
              <figure className="my-6 overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/e91a3796-6760-462f-8654-aecfa8de199f.png"
                  alt="Understanding PHQ-9 scores chart showing score ranges: 0-4 Minimal, 5-9 Mild, 10-14 Moderate, 15-19 Moderately Severe, and 20-27 Severe"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <figcaption className="p-3 text-center text-xs text-muted-foreground bg-muted/30">
                  PHQ-9 score interpretation guide — a screening score is not a
                  diagnosis by itself.
                </figcaption>
              </figure>
              <div className="my-6 overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose">
                <div className="bg-primary px-5 py-3">
                  <p className="text-white text-sm font-bold uppercase tracking-wider">
                    PHQ-9 Score Bands
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {[
                    ["0 to 4", "Minimal"],
                    ["5 to 9", "Mild"],
                    ["10 to 14", "Moderate"],
                    ["15 to 19", "Moderately severe"],
                    ["20 to 27", "Severe"],
                  ].map(([range, label]) => (
                    <div
                      key={range}
                      className="flex items-center justify-between px-5 py-3"
                    >
                      <span className="font-headings text-sm font-bold text-primary">
                        {range}
                      </span>
                      <span className="text-sm text-foreground/80">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ),
        },
        {
          body: <InlineEligibilityForm source="phq9-eligibility-intro" />,
        },
        {
          heading: "The GAD-7: measuring anxiety",
          body: (
            <div className="space-y-4">
              <p>
                The GAD-7 uses the same two-week window and the same 0-to-3
                scale, across seven items: feeling nervous, being unable to stop
                worrying, worrying too much about different things, trouble
                relaxing, restlessness, irritability, and feeling afraid
                something awful might happen.
              </p>
              <p>The total ranges from 0 to 21.</p>
              <figure className="my-6 overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/83b83b66-6532-415d-aa47-6ff630f9feba.png"
                  alt="Understanding GAD-7 scores chart showing score ranges: 0-4 Minimal, 5-9 Mild, 10-14 Moderate, and 15-21 Severe"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <figcaption className="p-3 text-center text-xs text-muted-foreground bg-muted/30">
                  GAD-7 score interpretation guide — measuring anxiety symptom
                  severity to support personalized care.
                </figcaption>
              </figure>
              <div className="my-6 overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose">
                <div className="bg-primary px-5 py-3">
                  <p className="text-white text-sm font-bold uppercase tracking-wider">
                    GAD-7 Score Bands
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {[
                    ["0 to 4", "Minimal"],
                    ["5 to 9", "Mild"],
                    ["10 to 14", "Moderate"],
                    ["15 to 21", "Severe"],
                  ].map(([range, label]) => (
                    <div
                      key={range}
                      className="flex items-center justify-between px-5 py-3"
                    >
                      <span className="font-headings text-sm font-bold text-primary">
                        {range}
                      </span>
                      <span className="text-sm text-foreground/80">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ),
        },
        {
          heading: "Why a 'moderate' score can come with a 'severe' diagnosis",
          body: (
            <div className="space-y-4">
              <p>
                This is where people get confused, and understandably so. You
                might score a 13 on the PHQ-9, land in the "moderate" band, and
                then see "Major depressive disorder, recurrent, severe" on your
                paperwork. How can both be true?
              </p>
              <figure className="my-6 overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/b5288457-af61-44b4-a308-e86cd8227098.png"
                  alt="PHQ-9 and GAD-7 at a glance comparison showing depression screening symptoms and anxiety screening symptoms"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <figcaption className="p-3 text-center text-xs text-muted-foreground bg-muted/30">
                  PHQ-9 and GAD-7 screening dimensions — these clinical tools
                  help start an informed treatment conversation.
                </figcaption>
              </figure>
              <p>The answer is that they're measuring different things.</p>
              <p>
                The PHQ-9 is a snapshot. It captures how frequently you've
                experienced symptoms over the past fourteen days. Your diagnosis
                is a clinical judgment that takes in the full picture: how long
                the depression has lasted, how many episodes you've had, how
                much it's affected your life, and how you've responded to
                treatment.
              </p>
              <p>
                Someone who is currently taking an antidepressant, seeing a
                therapist, and working hard to hold their life together may
                score in the moderate range precisely because the treatment is
                doing something. But if the depression has recurred multiple
                times, hasn't responded adequately to several medications, and
                has disrupted work, relationships, and health, a clinician may
                still classify it as severe. The score reflects two weeks. The
                diagnosis reflects years.
              </p>
              <p>
                There's also a pattern worth noticing in how people score.
                Someone might rate sleep, energy, and concentration as "nearly
                every day" but mood as only "more than half the days," and deny
                problems with self-esteem or thoughts of self-harm. That
                produces a moderate total, but the individual items tell a
                story: the physical and cognitive toll is heavy, even if the
                emotional distress is being held in check.
              </p>
            </div>
          ),
        },
        {
          heading: "What the scores are actually for",
          body: (
            <div className="space-y-4">
              <p>
                These questionnaires aren't a test you pass or fail, and they
                aren't the basis for your diagnosis. They serve three purposes.
              </p>
              <figure className="my-6 overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/9e9dc19d-0639-4846-8e87-ed8cd24e8464.png"
                  alt="How your scores can help: 5-step roadmap showing 1. Screen symptoms, 2. Open the conversation, 3. Track progress over time, 4. Guide treatment decisions, 5. Know when to reach out"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <figcaption className="p-3 text-center text-xs text-muted-foreground bg-muted/30">
                  How scores help: a clearer picture and a more informed path
                  forward with your provider.
                </figcaption>
              </figure>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Screening.</strong> They flag when someone should be
                  evaluated further.
                </li>
                <li>
                  <strong>Baseline.</strong> They give a starting point so
                  progress can be measured. This matters enormously for
                  treatments where change happens quickly and needs to be
                  tracked objectively.
                </li>
                <li>
                  <strong>Tracking.</strong> Repeated scores over time show
                  whether treatment is working. A drop from 13 to 4 is
                  meaningful. A score that sits at 13 for six months while you
                  cycle through medications is meaningful too, in a different
                  way.
                </li>
              </ul>
              <p>
                In depression research and in TMS clinics, remission is
                typically defined as a PHQ-9 score below 5. Not "a little
                better." Below 5. That's the target.
              </p>
            </div>
          ),
        },
        {
          heading: "When a stuck score becomes a decision point",
          body: (
            <div className="space-y-4">
              <p>
                If your PHQ-9 has hovered in the same range across two or more
                medication trials, that isn't a failure on your part. It's data.
                It tells you the treatments tried so far aren't reaching the
                problem.
              </p>
              <p>
                Antidepressants work on brain chemistry. But depression is also
                a circuitry problem. Brain imaging consistently shows that the
                region behind the forehead called the dorsolateral prefrontal
                cortex, which regulates mood, motivation, and attention, is
                underactive in depression. Medication changes the chemical
                environment around that circuit. It doesn't directly retrain it,
                which is why a score can plateau no matter how many times the
                dose is adjusted.
              </p>
              <p>
                Transcranial magnetic stimulation (TMS) works on the circuit
                itself, using focused magnetic pulses to stimulate that
                underactive region directly, with no anesthesia and nothing
                entering your bloodstream.
              </p>
            </div>
          ),
        },
        {
          heading: "What SAINT does to the numbers",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab. It's indicated for adults with
                major depressive disorder who haven't gotten adequate relief
                from antidepressants in their current episode, which is another
                way of saying: people whose scores haven't moved.
              </p>
              <SaintComparisonStatBand />
              <p>
                In the clinical trials behind its clearance, 79 percent of
                participants reached remission, with an average time to
                remission of 2.6 days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events. For comparison, remission rates in similar
                treatment-resistant populations run about 14 percent for another
                medication trial and 17 to 32 percent for standard TMS.
              </p>
              <p>
                Here's how it works at{" "}
                <a
                  href="/find-a-verified-saint-provider"
                  className="font-semibold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  a Verified SAINT Provider
                </a>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation (Zoom web conference).</strong> You meet
                  with our clinical team over a convenient Zoom call. As your
                  evaluation evolves, you will also have the option to meet with
                  our psychiatrist by Zoom web conference or in person at our
                  clinic. You meet with our clinical team, complete a fresh
                  PHQ-9 and GAD-7 for a baseline, and review your full history
                  to confirm you're a candidate.
                </li>
                <li>
                  <strong>fMRI brain scan (mandatory in person).</strong> A
                  roughly 45-minute functional MRI maps how your individual
                  brain networks are wired. (Mandatory in-person scan at an
                  imaging partner center.)
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil to that spot for
                  every session.
                </li>
                <li>
                  <strong>
                    Five-day stimulation series (mandatory in person).
                  </strong>{" "}
                  In-person treatment Monday through Friday at our Coral Springs
                  clinic. Ten 10-minute sessions per day, with 50-minute breaks
                  in between, for five consecutive days. Five clinic visits,
                  compared with 30 to 36 for conventional TMS. Your scores are
                  tracked throughout, so you and your care team can see the
                  change as it happens.
                </li>
              </ol>
              <p>
                Side effects were generally mild in trials: headache, fatigue,
                and temporary scalp discomfort. SAINT isn't appropriate for
                people with non-removable metal in or near the head or an
                implanted device, and you'll be screened at your consultation.
                You don't have to stop your current medications; most patients
                continue their existing regimen.
              </p>
            </div>
          ),
        },
        {
          heading: "What the scores can't tell you",
          body: (
            <div className="space-y-4">
              <p>
                They can't tell you why you're depressed. They can't distinguish
                between depression that will respond to medication and
                depression that won't. They can't measure how much effort you're
                spending to function, or how many years this has been going on.
                And a low score on the self-harm item does not mean your
                depression isn't serious.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If you've been watching your scores plateau, or if the gap
                between your "moderate" number and how you actually feel has
                been bothering you, bring that to a consultation.
              </p>
              <figure className="my-6 overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/f64da2ee-a178-41af-83c6-cda44cb51e5f.png"
                  alt="Patient reviewing PHQ-9 and GAD-7 screening results with a clinician during an evaluation consultation"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <figcaption className="p-3 text-center text-xs text-muted-foreground bg-muted/30">
                  A consultation is a conversation about your full history, not
                  just a score.
                </figcaption>
              </figure>
              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We use these tools the way
                they're meant to be used: as a starting point for a
                conversation, not a verdict. We'll review your scores alongside
                your full history and tell you honestly whether SAINT is likely
                to move them. We offer private outpatient care with a dedicated
                care team and weekend availability. Insurance coverage varies by
                plan; our team can help you understand your options, including
                prior authorization and self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, please
                call or text 988 (Suicide and Crisis Lifeline) or go to your
                nearest emergency room. This article is for educational purposes
                and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
        {
          body: <InlineEligibilityForm source="phq9-eligibility-end" />,
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
